import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Progress, Skeleton, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { PNG } from '@app/constants';

import { LONG_WAIT_HINTS_AUTH } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/constants';
import { useLongWaitHint } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/hooks';
import {
  AuthStepState,
  type AuthStepInfo,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';
import { LongWaitNotice } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/LongWaitNotice';

import type { FC, ReactNode } from 'react';

const { Text } = Typography;

const AUTH_PROGRESS_STEPS = [12, 18, 25, 33, 42, 51, 60, 68, 76, 83, 88, 92];
const AUTH_PROGRESS_LOOP_START_INDEX = 3;
const AUTH_PROGRESS_TICK_MS = 1_250;

interface AuthorizationProgressProps {
  /** Подпись под кружком; у QR-формы её место — под самим макетом */
  label?: string;
  qrShape?: boolean;
}

/**
 * Ненавязчивый индикатор ожидания, а не обещание точного времени. За полный
 * цикл он доходит только до 92%, после чего продолжает с промежуточного шага:
 * до фактического ответа интерфейс никогда не показывает ложные 100%.
 */
const AuthorizationProgress: FC<AuthorizationProgressProps> = ({
  label,
  qrShape = false,
}) => {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setStepIndex((currentIndex) =>
        currentIndex === AUTH_PROGRESS_STEPS.length - 1
          ? AUTH_PROGRESS_LOOP_START_INDEX
          : currentIndex + 1
      );
    }, AUTH_PROGRESS_TICK_MS);

    return () => window.clearInterval(interval);
  }, []);

  const progress = AUTH_PROGRESS_STEPS[stepIndex];
  const indicator = (
    <div
      className="connect-integration-modal__progress-indicator"
      data-testid="connect-auth-progress"
      data-progress={progress}
    >
      <Progress
        type="circle"
        percent={progress}
        showInfo={false}
        size={80}
        strokeWidth={7}
      />
      {label ? <Text type="secondary">{label}</Text> : null}
    </div>
  );

  if (!qrShape) {
    return indicator;
  }

  return (
    <div
      className="connect-integration-modal__qr-progress"
      data-testid="connect-auth-qr-progress"
    >
      {/* Силуэт кода вместо серого прямоугольника: угловые метки и сетка
          модулей — на этом месте появится именно QR (issue/11235). */}
      <div className="connect-integration-modal__qr-skeleton" aria-hidden>
        <span className="connect-integration-modal__qr-skeleton-modules" />
        <span className="connect-integration-modal__qr-skeleton-corner connect-integration-modal__qr-skeleton-corner--top-left" />
        <span className="connect-integration-modal__qr-skeleton-corner connect-integration-modal__qr-skeleton-corner--top-right" />
        <span className="connect-integration-modal__qr-skeleton-corner connect-integration-modal__qr-skeleton-corner--bottom-left" />
      </div>
      {indicator}
    </div>
  );
};

export interface AuthStepViewProps {
  stepInfo: AuthStepInfo;
  /** Подсказки для затянувшегося ожидания — свои у каждого шага (task/10903) */
  longWaitHints?: string[];
  /** PNG в base64 — из GetQr; пустой, пока код не пришёл */
  qr?: string;
  /** Инструкция канала слева от QR (нумерованные шаги) */
  instruction?: ReactNode;
  /** Контент канальных форм (номер/код/пароль/токен) — рендерит 10730 */
  channelContent?: ReactNode;
  onRefreshQr?(): void;
  onRetry?(): void;
  /** Ожидание после скана вышло за предел — показать причину и действия */
  isScannedWaitOver?: boolean;
  /** Запасной путь входа, когда подтверждение так и не пришло */
  onSwitchToPhone?(): void;
  /** Для QR-архетипа загрузка выглядит как будущий QR, а не полоса контента */
  showQrProgress?: boolean;
}

/**
 * Универсальные состояния шага «Авторизация» единого окна (task/10729).
 * Ни одного немого экрана: у каждого состояния — текст и одно действие.
 * Канальные формы приходят через channelContent (task/10730, task/10731).
 */
export const AuthStepView: FC<AuthStepViewProps> = ({
  stepInfo,
  qr,
  instruction,
  channelContent,
  onRefreshQr,
  onRetry,
  isScannedWaitOver,
  onSwitchToPhone,
  showQrProgress,
  longWaitHints = LONG_WAIT_HINTS_AUTH,
}) => {
  const isLoading = stepInfo.state === AuthStepState.Loading;
  const { hint } = useLongWaitHint({
    isWaiting: isLoading,
    hints: longWaitHints,
  });

  if (isLoading) {
    if (showQrProgress) {
      // Тот же макет, что и с готовым кодом: инструкция на месте (пока
      // приглушена и мигает в такт скелетону), справа — место под QR, а все
      // сообщения о загрузке ниже (issue/11235).
      return (
        <div
          className="connect-integration-modal__qr-state"
          data-testid="connect-auth-loading"
        >
          <div className="connect-integration-modal__qr-layout">
            <div className="connect-integration-modal__instruction connect-integration-modal__instruction--loading">
              {instruction}
            </div>
            <div className="connect-integration-modal__qr-box">
              <AuthorizationProgress qrShape />
            </div>
          </div>
          <div className="connect-integration-modal__qr-state-messages">
            <Text type="secondary">Подготавливаем QR-код</Text>
            <Text type="secondary">
              Это может занять немного времени. Окно обновится автоматически.
            </Text>
            {hint ? <LongWaitNotice hint={hint} /> : null}
          </div>
        </div>
      );
    }

    return (
      <div className="connect-integration-modal__state">
        <Skeleton active data-testid="connect-auth-loading" />
        {hint ? <LongWaitNotice hint={hint} /> : null}
      </div>
    );
  }

  if (stepInfo.state === AuthStepState.Error) {
    return (
      <div className="connect-integration-modal__state">
        <Alert
          type="error"
          showIcon
          message="Не получилось подключить"
          description={
            stepInfo.errorMessage ??
            'Сервис сейчас недоступен. Обычно это проходит за минуту.'
          }
        />
        {onRetry && (
          <Button onClick={onRetry} data-testid="connect-auth-retry">
            Попробовать ещё раз
          </Button>
        )}
      </div>
    );
  }

  if (stepInfo.state === AuthStepState.Success) {
    return (
      <div
        className="connect-integration-modal__state"
        data-testid="connect-auth-success"
      >
        <CheckCircleFilled className="connect-integration-modal__success-icon" />
        <Text strong>Интеграция подключена</Text>
        <Text type="secondary">
          Канал появится в реестре со статусом «Работает».
        </Text>
      </div>
    );
  }

  if (stepInfo.state === AuthStepState.QrScanned) {
    // Подтверждение так и не пришло: молчать дальше нельзя — у человека
    // должны быть причина и выход (issue/11036).
    if (isScannedWaitOver) {
      return (
        <div
          className="connect-integration-modal__state"
          data-testid="connect-auth-scanned-timeout"
        >
          <Alert
            type="warning"
            showIcon
            message="Вход не завершился"
            description="Код принят, но подтверждение не пришло. Проверьте телефон — возможно, там ждёт запрос пароля."
          />
          {onRetry && (
            <Button
              type="primary"
              onClick={onRetry}
              data-testid="connect-auth-scanned-retry"
            >
              Попробовать снова
            </Button>
          )}
          {onSwitchToPhone && (
            <Button
              onClick={onSwitchToPhone}
              data-testid="connect-auth-scanned-phone"
            >
              Войти по номеру
            </Button>
          )}
        </div>
      );
    }

    return (
      <div
        className="connect-integration-modal__state"
        data-testid="connect-auth-scanned"
      >
        <AuthorizationProgress label="Завершаем подключение" />
        <Text strong>Код отсканирован</Text>
        <Text type="secondary">
          Подтвердите вход на телефоне. Если включён пароль — окно попросит его
          следующим шагом.
        </Text>
      </div>
    );
  }

  if (
    stepInfo.state === AuthStepState.QrReady ||
    stepInfo.state === AuthStepState.QrExpired
  ) {
    return (
      <div className="connect-integration-modal__qr-layout">
        <div className="connect-integration-modal__instruction">
          {instruction}
        </div>
        <div className="connect-integration-modal__qr-box">
          {qr ? (
            <img
              src={`data:${PNG};base64,${qr}`}
              alt="qr-code"
              className="connect-integration-modal__qr-image"
              data-testid="connect-auth-qr"
            />
          ) : (
            <AuthorizationProgress qrShape />
          )}
          {stepInfo.state === AuthStepState.QrExpired ? (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={onRefreshQr}
              data-testid="connect-auth-refresh-qr"
            >
              Показать новый код
            </Button>
          ) : (
            <Text type="secondary">
              Код обновляется сам — просто отсканируйте его.
            </Text>
          )}
        </div>
      </div>
    );
  }

  // CodeSent / Password2fa: конкретные формы каналов рендерит task/10730
  return (
    <div className="connect-integration-modal__state">{channelContent}</div>
  );
};
